<template>
  <el-tree
    v-if="modelValue"
    ref="tree"
    :data="activeTreeData"
    :props="defaultProps"
    node-key="uuid"
    :allow-drag="allowDrag"
    :allow-drop="allowDrop"
    draggable
    show-checkbox
    default-expand-all
    :check-on-click-node="false"
    :expand-on-click-node="false"
    :default-checked-keys="defaultExpandKeys"
    @check-change="handleCheckChange"
    @node-click="handleNodeClick"
    @node-drop="handleNodeDrop"
  >
    <template #default="{ node, data }">
      <div class="tree-node-row" @click.stop="handleNodeClick(data, node)">
        <div class="tree-node-label">
          {{ node.label }}
        </div>
        <div v-if="!data.isNone" class="tree-node-icons">
          <span
            v-if="canCalculateResult && data.isLine"
            class="icon-btn"
            @click="calculateResult(data)"
          >
            <font-awesome-icon icon="check" />
          </span>

          <span
            v-if="!data.isCamera && !data.isLine"
            class="icon-btn"
            :class="{ active: data.showWireframe }"
            @click="toggleWireframe(data)"
          >
            <font-awesome-icon :icon="['fac', 'mesh']" />
          </span>

          <span
            v-if="!data.isCamera && !data.isLine"
            class="icon-btn"
            :class="{ active: data.opacity !== 100 }"
            @click="toggleOpacity(data)"
          >
            <font-awesome-icon :icon="['fac', 'opacity']" />
          </span>

          <span class="icon-btn" @click="removeItem(data)">
            <font-awesome-icon icon="trash" />
          </span>
        </div>
      </div>
    </template>
  </el-tree>
  <div class="flexRemaining"></div>
  <div class="treeButtons">
    <el-button type="primary" circle @click="router.push(addUrl)">
      <font-awesome-icon icon="plus" />
    </el-button>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-facing-decorator';
import type Node from 'element-plus/es/components/tree/src/model/node';
import type { AllowDropType } from 'element-plus/es/components/tree/src/tree.type';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';

import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import { HistoryOperationType } from '@/types/ui/HistoryList';
import * as componentService from '@/services/api/componentService';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const customNodeClass = (data: MeshTreeDataItem) => {
  if (data.isSelected) {
    return 'selected';
  }
  return null;
};

@Component({
  components: {},
  emits: ['deleteItem', 'calculateResult'],
})
/* eslint-disable @typescript-eslint/no-explicit-any*/
export default class MeshTree extends Vue {
  @Prop() modelValue!: MeshTreeData;
  @Prop({ default: '' }) addUrl!: string;
  @Prop({ default: false }) canCalculateResult!: boolean;
  router = useRouter();
  defaultProps = {
    children: 'activeChildren',
    label: 'name',
    class: customNodeClass,
  };
  activeTreeData: MeshTreeDataItem[] = [];
  visibleItemKeys: string[] = [];

  @Watch('modelValue.lastUpdateStructure', { immediate: false, deep: false })
  async onModelValueStructureChanged(): Promise<void> {
    const previousItems = [...this.visibleItemKeys];
    const hasPreviousSelection = this.activeTreeData.length > 0;
    const tree = this.$refs.tree as any;
    const oldSelection = [...tree.getCheckedKeys()];
    this.activeTreeData = this.modelValue.getActiveTreeData();

    let checkedKeys: string[] = [];
    const getCheckedKeys = (items: MeshTreeDataItem[]): void => {
      items.forEach((item) => {
        if (item.visible) {
          checkedKeys.push(item.uuid);
        }
        if (item.activeChildren) getCheckedKeys(item.activeChildren);
      });
    };
    getCheckedKeys(this.activeTreeData);

    this.visibleItemKeys = [...checkedKeys];
    if (hasPreviousSelection) {
      const newItems = checkedKeys.filter((x) => !previousItems.includes(x));
      checkedKeys = [...oldSelection, ...newItems];
    }

    setTimeout(() => {
      if (tree) {
        tree.setCheckedKeys(checkedKeys);
      }
    }, 100);
  }

  handleCheckChange(data: MeshTreeDataItem, checked: boolean, indeterminate: boolean): void {
    data.visible = checked || indeterminate;
  }

  handleNodeClick(data: MeshTreeDataItem, node: Node): void {
    node.expanded = true;
    this.modelValue.selectItem(data, true, true);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  handleNodeDrop(dragNode: any): void {
    const itemId = dragNode.data.uuid;
    const parent = this.modelValue.drag(itemId);
    const newParentId = parent.newParent ? parent.newParent.uuid : null;
    const newIndex = parent.newIndex;
    const oldParentId = parent.oldParent ? parent.oldParent.uuid : null;
    const oldIndex = parent.oldIndex;
    const historyMeshIdList: string[] = [itemId];
    if (parent.newParent) historyMeshIdList.push(parent.newParent.uuid);
    if (parent.oldParent) historyMeshIdList.push(parent.oldParent.uuid);
    if (this.modelValue.modelId) {
      componentService.changeParent(
        this.modelValue.modelId,
        dragNode.data.componentId,
        itemId,
        parent.newParent ? parent.newParent.componentId : null,
        parent.newParent ? parent.newParent.uuid : null
      );
    }
    this.modelValue.historyList.addList(
      historyMeshIdList,
      HistoryOperationType.hierarchy,
      async () => {
        this.modelValue.setParent(itemId, oldParentId, oldIndex);
        this.onModelValueStructureChanged();
      },
      async () => {
        this.modelValue.setParent(itemId, newParentId, newIndex);
        this.onModelValueStructureChanged();
      }
    );
  }

  allowDrag(draggingNode: Node): boolean {
    return draggingNode.level > 1;
  }

  allowDrop(draggingNode: Node, dropNode: Node, type: AllowDropType): boolean {
    return type === 'inner' || dropNode.level > 1;
  }

  get defaultExpandKeys(): number[] {
    return this.activeTreeData.map((value, index) => {
      return index;
    });
  }

  removeItem(item: MeshTreeDataItem): void {
    ElMessageBox.confirm((this as any).$t('confirm.delete.message'), (this as any).$t('confirm.delete.title'), {
      confirmButtonText: (this as any).$t('confirm.delete.ok'),
      cancelButtonText: (this as any).$t('confirm.delete.cancel'),
      type: 'warning',
    }).then(() => {
      this.$emit('deleteItem', item);
      this.modelValue.removeItem(item).then((result) => {
        if (!result) {
          ElMessage.error((this as any).$t('error.api.delete'));
        } else {
          ElMessage.success((this as any).$t('success.api.delete'));
        }
      });
    });
  }

  calculateResult(item: MeshTreeDataItem): void {
    ElMessageBox.confirm((this as any).$t('confirm.calculateResult.message'), (this as any).$t('confirm.calculateResult.title'), {
      confirmButtonText: (this as any).$t('confirm.calculateResult.ok'),
      cancelButtonText: (this as any).$t('confirm.calculateResult.cancel'),
      type: 'warning',
    }).then(() => {
      this.$emit('calculateResult', item);
    });
  }

  toggleWireframe(item: MeshTreeDataItem): void {
    item.showWireframe = !item.showWireframe;
  }

  toggleOpacity(item: MeshTreeDataItem): void {
    item.opacity = item.opacity === 100 ? 50 : 100;
  }
}
</script>

<style lang="scss" scoped>
.el-tree :deep() {
  background-color: unset;
  margin: 1rem;
}

.selected .level-left {
  color: var(--color-primary);
}

.active {
  color: var(--color-primary);
}
</style>

<style lang="scss">
@use '@/assets/styles/variables.scss';
.selected .level-left {
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
}

.treeButtons {
  text-align: right;
  margin: 1rem;
}

.level-left.node-name {
  width: 60%;
  overflow: hidden;
}

.tree-node-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 0.5rem;
  gap: 0.5rem;
}

.tree-node-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.tree-node-icons {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.icon-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.icon-btn.active {
  color: var(--el-color-primary); /* Or your theme color */
}
</style>
